/** CCS Spotlight — public magazine page. Celebrating excellence in action. */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { LogoMark } from "@/components/brand/Logo";
import LegalFooter from "@/components/brand/LegalFooter";
import SpotlightArticle from "@/components/spotlight/SpotlightArticle";
import NominationDialog from "@/components/spotlight/NominationDialog";
import { categoryBadge } from "@/components/spotlight/spotlightUtils";

const EASE = [0.22, 1, 0.36, 1];

/** Fallback only if the API is unreachable — the gallery itself is managed
 * from the admin dashboard (Spotlight → Hero Images). */
const FALLBACK_HERO_IMAGES = [
  {
    id: "fallback-1",
    src: "https://images.unsplash.com/photo-1556740738-b6a63e27c4df?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    alt: "A customer service representative warmly assisting a customer",
    label: "Warm, attentive service",
  },
];

const ROTATE_MS = 5500;

/** Editorial rotating hero — images are admin-managed via /app/spotlight → Hero Images. */
const SpotlightHeroCarousel = () => {
  const [images, setImages] = useState(FALLBACK_HERO_IMAGES);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    api
      .get("/public/spotlight-hero-images")
      .then((r) => {
        const imgs = r.data.images || [];
        if (imgs.length > 0) setImages(imgs);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % images.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [images]);

  const current = images[Math.min(index, images.length - 1)];

  return (
    <div data-testid="spotlight-hero-carousel">
      <div className="relative">
        {/* offset gold frame */}
        <div className="absolute -top-4 -left-4 w-full h-full border border-[#C28A2E]/40 rounded-sm" />
        <div className="relative w-full aspect-[4/3] overflow-hidden rounded-sm border border-[#2A1810]/5 bg-[#F6F3EB]">
          {images.map((img, i) => (
            <motion.img
              key={img.id || img.src}
              src={img.src}
              alt={img.alt}
              initial={false}
              animate={{ opacity: i === index ? 1 : 0, scale: i === index ? 1 : 1.04 }}
              transition={{ opacity: { duration: 1.4, ease: "easeInOut" }, scale: { duration: 6.5, ease: "linear" } }}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "saturate(0.92) sepia(0.10) brightness(1.02)" }}
              data-testid={`hero-image-${i}`}
            />
          ))}
          {/* warm brand grading + gentle spotlight vignette */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#2A1810]/45 via-transparent to-[#C28A2E]/10 pointer-events-none" />
        </div>
      </div>

      {/* caption + rotation dots */}
      <div className="mt-5 flex items-center justify-between gap-4">
        <AnimatePresence mode="wait">
          <motion.p
            key={index}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B5A2B]"
            data-testid="hero-image-label"
          >
            <span className="text-[#C28A2E] mr-2">✦</span>
            {current?.label}
          </motion.p>
        </AnimatePresence>
        <div className="flex items-center gap-2" data-testid="hero-carousel-dots">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Show image ${i + 1}`}
              className={`rounded-full transition-all duration-500 ${
                i === index ? "w-5 h-1.5 bg-[#C28A2E]" : "w-1.5 h-1.5 bg-[#C28A2E]/30 hover:bg-[#C28A2E]/60"
              }`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export const SpotlightHeader = () => (
  <header className="border-b border-[#EBE3D2] bg-[#FDFBF5]/90 backdrop-blur-xl sticky top-0 z-30">
    <div className="max-w-7xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3" data-testid="spotlight-header-logo">
        <LogoMark size={40} />
        <div className="leading-tight">
          <div className="font-display font-semibold text-[#2A1810] text-sm">The CCS Approach</div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">CCS Spotlight</div>
        </div>
      </Link>
      <nav className="flex items-center gap-5 text-sm">
        <Link to="/" className="text-[#6B5944] hover:text-[#5D3A1F]" data-testid="spotlight-nav-home">Home</Link>
        <Link to="/book" className="hidden sm:inline text-[#6B5944] hover:text-[#5D3A1F]" data-testid="spotlight-nav-explore">Explore</Link>
        <Link to="/testimonials" className="hidden sm:inline text-[#6B5944] hover:text-[#5D3A1F]" data-testid="spotlight-nav-testimonials">Testimonials</Link>
      </nav>
    </div>
  </header>
);

export const NominationCTA = ({ onNominate }) => (
  <section className="bg-[#2A1810] rounded-2xl px-8 sm:px-14 py-14 text-center" data-testid="nomination-cta">
    <div className="text-xs uppercase tracking-[0.3em] font-semibold text-[#F5D88A] mb-4">Celebrate someone</div>
    <h3 className="font-display text-2xl sm:text-3xl font-light text-[#FDFBF5] max-w-2xl mx-auto leading-snug">
      Have you experienced exceptional leadership or customer service?
    </h3>
    <p className="mt-4 text-[#F0E6D2]/75 text-sm sm:text-base max-w-xl mx-auto">
      Know someone who deserves to be recognized? Nominate them for a future CCS Spotlight.
    </p>
    <button
      onClick={onNominate}
      className="mt-8 bg-[#C28A2E] text-[#2A1810] px-8 py-3.5 rounded-full text-sm font-bold tracking-wide hover:bg-[#E7B54D] transition-colors duration-300"
      data-testid="submit-nomination-button"
    >
      Submit a Nomination
    </button>
  </section>
);

const ArchiveCard = ({ s, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease: EASE }}
  >
    <Link to={`/spotlight/${s.id}`} className="group block" data-testid={`archive-card-${s.id}`}>
      <div className="overflow-hidden rounded-sm border border-[#2A1810]/5 bg-[#F6F3EB]">
        <img
          src={s.image_url}
          alt={s.honoree_name}
          className="w-full aspect-[4/3] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
      </div>
      <div className="mt-5">
        <span className={`inline-block text-[10px] uppercase tracking-[0.16em] font-bold px-2.5 py-1 rounded-full ${categoryBadge(s.category)}`}>
          {s.category}
        </span>
        <h4 className="mt-3 font-display text-xl font-medium text-[#2A1810] leading-snug transition-colors duration-300 group-hover:text-[#C28A2E]">
          {s.title || s.honoree_name}
        </h4>
        <p className="mt-1 text-sm text-[#5D3A1F] font-medium">{s.honoree_name}</p>
        <p className="mt-2 text-sm text-[#6B5944] leading-relaxed line-clamp-2">{s.summary}</p>
        <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#5D3A1F] group-hover:text-[#C28A2E] transition-colors duration-300">
          Read More <ArrowRight size={13} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  </motion.div>
);

export default function SpotlightPage() {
  const [spotlights, setSpotlights] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [nominateOpen, setNominateOpen] = useState(false);

  useEffect(() => {
    api
      .get("/public/spotlights")
      .then((r) => {
        setSpotlights(r.data.spotlights || []);
        setCategories(r.data.categories || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const featured = spotlights[0];
  const archive = useMemo(() => {
    let list = spotlights.slice(1);
    if (category !== "All") list = list.filter((s) => s.category === category);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((s) =>
        [s.honoree_name, s.organization, s.title, s.summary, s.what_happened, s.ccs_lesson]
          .join(" ")
          .toLowerCase()
          .includes(needle)
      );
    }
    return list;
  }, [spotlights, category, q]);

  return (
    <div className="min-h-screen bg-[#FDFBF5]" data-testid="spotlight-page">
      <SpotlightHeader />

      {/* ============== Hero ============== */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pt-20 sm:pt-28 pb-16 sm:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <motion.div
            className="lg:col-span-6"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <div className="text-xs sm:text-sm uppercase tracking-[0.3em] font-semibold text-[#C28A2E]">
              The CCS Approach presents
            </div>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl tracking-tight leading-none font-light text-[#2A1810]" data-testid="spotlight-hero-title">
              CCS Spotlight
            </h1>
            <p className="mt-4 font-display text-xl sm:text-2xl text-[#B7541E] italic font-light">
              Celebrating Excellence in Action
            </p>
            <p className="mt-7 text-base sm:text-lg leading-[1.85] text-[#2A1810]/75 max-w-xl">
              Every day, individuals and organizations choose to communicate with intention,
              collaborate with purpose and serve with excellence. CCS Spotlight celebrates those
              moments, because excellence deserves to be recognized and shared.
            </p>
          </motion.div>
          <motion.div
            className="lg:col-span-6"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          >
            <SpotlightHeroCarousel />
          </motion.div>
        </div>
      </section>

      {/* ============== Featured Spotlight ============== */}
      <section className="border-t border-[#EBE3D2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16 sm:py-24">
          <div className="text-xs uppercase tracking-[0.3em] font-semibold text-[#C28A2E] mb-10">
            Latest Spotlight
          </div>
          {loading ? (
            <div className="py-24 text-center text-[#6B5944]" data-testid="spotlight-loading">Loading…</div>
          ) : featured ? (
            <SpotlightArticle spotlight={featured} onNominate={() => setNominateOpen(true)} />
          ) : (
            <div className="py-24 text-center text-[#6B5944]" data-testid="spotlight-empty">
              The first Spotlight story is on its way.
            </div>
          )}
        </div>
      </section>

      {/* ============== Nomination CTA ============== */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pb-16 sm:pb-24">
        <NominationCTA onNominate={() => setNominateOpen(true)} />
      </section>

      {/* ============== Previous Spotlights ============== */}
      <section className="border-t border-[#EBE3D2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16 sm:py-24">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] font-semibold text-[#C28A2E]">The archive</div>
              <h3 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight text-[#2A1810]">
                Previous Spotlights
              </h3>
            </div>
            <div className="relative w-full lg:w-72">
              <Search size={16} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#6B5944]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search stories…"
                className="w-full bg-transparent border-0 border-b border-[#2A1810]/20 rounded-none focus:border-[#C28A2E] focus:outline-none focus:ring-0 pl-7 pr-0 py-2.5 text-sm text-[#2A1810] placeholder:text-[#2A1810]/35"
                data-testid="spotlight-search-input"
              />
            </div>
          </div>

          {/* Category filter — elegant text links */}
          <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3" data-testid="spotlight-category-filter">
            {["All", ...categories].map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`text-sm pb-1.5 border-b-2 transition-colors duration-300 ${
                  category === c
                    ? "border-[#C28A2E] text-[#2A1810] font-semibold"
                    : "border-transparent text-[#2A1810]/50 hover:text-[#2A1810]"
                }`}
                data-testid={`category-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </div>

          {archive.length > 0 ? (
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16" data-testid="spotlight-archive-grid">
              {archive.map((s, i) => (
                <ArchiveCard key={s.id} s={s} i={i} />
              ))}
            </div>
          ) : (
            <div className="mt-14 py-16 text-center border border-dashed border-[#EBE3D2] rounded-2xl" data-testid="archive-empty">
              <p className="text-[#6B5944]">
                {q || category !== "All"
                  ? "No spotlights match your search just yet."
                  : "This library of inspiring stories is just beginning. New Spotlights will appear here as they are published."}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#EBE3D2] py-10 px-6 text-center">
        <p className="text-xs italic text-[#8B5A2B]">"Let everything you do be done in love." — 1 Corinthians 16:14</p>
        <LegalFooter className="mt-6" />
        <p className="mt-2 text-[11px] text-[#6B5944]/70">© The CCS Approach Consultancy Services · Communicate · Collaborate · Serve</p>
      </footer>

      <NominationDialog open={nominateOpen} onOpenChange={setNominateOpen} />
    </div>
  );
}
